import { HttpErrorResponse, HttpResponseBase } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';

import { DgError } from '@app/shared/models/dg-error';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateService } from '@ngx-translate/core';

import { DgCaptchaError, isDgCaptchaError } from '../models/dg-captcha-error';
import {
  CheckEmailResponse,
  ResetPasswordResponse,
  SpecialInstruction,
  SpecialInstructionType,
} from '@app/account/account-api.model';
import { UserProfile } from '@app/user/user-api.model';

// Header values present in captcha request
const captchaTokenHeaderName = 'X-CaptchaToken';

// Header values present in a captcha response
const showCaptchaHeaderName = 'X-ShowCaptcha';
const validUserResponseTokenHeaderName = 'X-ValidToken';

/**
 * Account Service
 *
 * Utilities for creating and managing user accounts in the Degreed Application.
 *
 * Viewing and managing the current Authenticated user may be done via @see AuthService
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  public readonly i18n = this.translate.instant([
    'AccountSvc_DeleteAccount',
    'AccountSvc_LoginError',
    'AccountSvc_OrgPasswordError',
    'AccountSvc_PasswordResetError',
    'AccountSvc_PasswordResetInstructionsMessage',
  ]);

  /**
   * @deprecated consumers of the AccountService should manage their own i18n resources
   * TODO: remove after Angular migration if not used directly
   */
  public readonly messageHtml: Record<SpecialInstructionType, string> = {
    EmailConfirmation: this.translate.instant('AccountSvc_EmailConfirmation'),
    ChangeLinkFail: this.translate.instant('AccountSvc_ChangeLinkFail'),
    ChangeSuccess: this.translate.instant('AccountSvc_ChangeSuccess'),
    DefaultPassword: this.translate.instant('AccountSvc_DefaultPassword'),
    ForgotPassword: this.translate.instant('AccountSvc_ForgotPassword'),
    GroupInvite: this.translate.instant('AccountSvc_GroupInvite'),
    None: this.translate.instant('AccountSvc_None'),
    OrgInvite: this.translate.instant('AccountSvc_OrgInvite'),
    OutcomeReview: this.translate.instant('AccountSvc_OutcomeReview'),
    PasswordExpired: this.translate.instant('PasswordExpired'),
    PasswordResetError: this.translate.instant('AccountSvc_PasswordResetError'),
    ProfileComparison: this.translate.instant('ProfileComparison'),
    Restricted: this.translate.instant('AccountSvc_Restricted'),
    SignUp: this.translate.instant('AccountSvc_SignUp'),
    SessionExpired: this.translate.instant('AccountSvc_SessionExpired'),
    UserEndorsement: this.translate.instant('AccountSvc_UserEndorsement'),
  };

  /**
   * @deprecated do not use this value directly. Use isValidEmail wrapper instead
   * TODO: remove after Angular migration if not used directly
   */
  public readonly emailPattern = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  /**
   * @deprecated do not use this value directly. Use isValidPassword wrapper instead
   * TODO: remove after Angular migration if not used directly
   */
  public readonly passwordPattern = new RegExp(
    /(?=.{8,}$)^((?=.*[a-zA-Z])(?=.*\d)(?!.*\s).*$)/
  );

  constructor(
    private authService: AuthService,
    private http: NgxHttpClient,
    private tracker: TrackerService,
    private translate: TranslateService
  ) {}

  /**
   * Is the given server special instruction one that we
   * associate with an error response?
   */
  public isAccountError(message: string): boolean {
    return (
      message === SpecialInstruction.ChangeLinkFail ||
      message === SpecialInstruction.PasswordResetError ||
      message === SpecialInstruction.Restricted ||
      message === SpecialInstruction.SessionExpired
    );
  }

  /**
   * Is the given email a valid RFC 5322 email address?
   *
   * @link https://tools.ietf.org/html/rfc5322#section-3.4.1
   */
  public isValidEmail(email?: string): boolean {
    if ('string' !== typeof email) return false;

    // validation constants
    const emailMaxLocalLength = 64;
    const emailMaxDomainLength = 253;

    // Check cases not covered by the email regex
    const [local, domain] = email.split('@');
    if (
      !local ||
      !domain ||
      local.length > emailMaxLocalLength ||
      domain.length > emailMaxDomainLength ||
      domain.includes('_') ||
      local.includes('/')
    ) {
      return false;
    }

    return this.emailPattern.test(email);
  }

  /**
   * Is the given password 'strong' enough to be considered a valid password?
   */
  public isValidPassword(password?: string): boolean {
    if ('string' !== typeof password) return false;

    return this.passwordPattern.test(password);
  }

  /**
   * Create a new account with the given user details
   *
   * @param firstname first name for the user
   * @param lastname  last name for the user
   * @param email     email address for the user
   * @param password  The initial temporary password for the user to use. A user will be prompted
   *                  to change the password on the first login
   *
   * @returns the newly created user profile
   */
  public createAccount(
    firstname: string,
    lastname: string,
    email: string,
    password: string
  ): Observable<UserProfile> {
    return this.http
      .post<UserProfile>('/user/RegisterAccount', {
        firstname,
        lastname,
        email,
        password,
      })
      .pipe(
        tap(() => {
          // NOTE: this tracking event was disabled in the ajs component due to previous csrf
          // issues, and may need to be removed again if it causes issues
          this.tracker.trackEventData({
            action: 'Account Created',
            category: 'User',
          });
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          // NOTE: this check was added to the ajs, possibly to deal with errors around CORS
          // if it is no longer needed, it could be removed
          if (errorResponse.status === 0) {
            return of(errorResponse.error);
          }

          return throwError(
            new DgError(
              this.translate.instant('AccountSvc_CreateAccountError') +
                ' ' +
                errorResponse.message,
              errorResponse
            )
          );
        })
      );
  }

  /**
   * Delete the account of the requesting authenticated user
   */
  public deleteAccount(): Observable<void> {
    return this.http.post<void>('/user/DeleteUserAccount', {}).pipe(
      tap(() => {
        this.tracker.trackEventData({
          action: 'Account Deleted',
          category: 'User',
        });
      }),
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(
          new DgError(
            this.translate.instant('AccountSvc_DeleteAccount'),
            errorResponse
          )
        )
      )
    );
  }

  /**
   * Check if the given email address exists in the system, and return info about the account
   *
   * NOTE: this varies from the AngularJS version of the service, in that it does not return
   * the raw HTTP response on success, and therefore handles the checks for captcha errors
   * internally
   *
   * @param email        the user account or email address to check
   * @param captchaToken token from recaptcha (if present in page)
   * @param scope        scope is determined from web app (null), or native mobile with web view.
   * @param returnUrl    the url to return after sso or other redirects
   *
   * @returns a response describing the user from the check email call, or a captcha error when
   *          captcha headers are present in the response
   */
  public checkEmail(
    email: string,
    captchaToken: string,
    scope: string = null,
    returnUrl?: string
  ): Observable<CheckEmailResponse> {
    const headers = {};

    // only set headers if value is present
    if (captchaToken) {
      headers[captchaTokenHeaderName] = captchaToken;
    }

    return this.http
      .post<CheckEmailResponse>(
        '/degreed/checkemail',
        { email, scope, returnUrl },
        { headers, observe: 'response' }
      )
      .pipe(
        map((response) => {
          const { showCaptcha, validToken } = this.getCaptchaValues(response);
          if (showCaptcha) {
            throw new DgCaptchaError(showCaptcha, validToken);
          }

          return response.body;
        }),
        catchError((errorResponse: HttpErrorResponse | DgCaptchaError) => {
          // Re-throw the captcha error from here
          if (isDgCaptchaError(errorResponse)) {
            return throwError(errorResponse);
          }

          // Else, to check if we should throw the captcha error
          const { showCaptcha, validToken } =
            this.getCaptchaValues(errorResponse);
          if (showCaptcha) {
            return throwError(new DgCaptchaError(showCaptcha, validToken));
          }

          // NOTE: The `status == 0` check was added to the ajs, possibly to deal with errors
          // around CORS. If it is no longer needed, it could be removed
          if (errorResponse.status !== 0 && errorResponse.status !== 400) {
            return throwError(
              new DgError(
                this.translate.instant('AccountSvc_LoginError'),
                errorResponse
              )
            );
          }

          // HACK: Not wrapping 400 (Bad Request) errors in DgError to prevent showing a toast
          return throwError(errorResponse);
        })
      );
  }

  /**
   * Trigger a password reset for the given user
   *
   * @param username email or username identifier for the user to reset
   */
  public resetPassword(username: string): Observable<void> {
    return this.http
      .post<void>('/degreed/forgotpassword', {
        username,
      })
      .pipe(
        tap(() => {
          this.tracker.trackEventData({
            action: 'Forgot Password',
            category: 'User',
          });
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          // NOTE: this check was added to the ajs, possibly to deal with errors around CORS
          // if it is no longer needed, it could be removed
          if (errorResponse.status === 0) return of(null);

          return throwError(errorResponse.error.message);
        })
      );
  }

  /**
   * Reset the password for a user
   *
   * @param email - the email for the user
   * @param code - code from the email 'Reset Password' link
   * @param password
   */
  public setResetPassword(
    code: string,
    email: string,
    password: string,
    confirmPassword: string
  ): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>('/account/resetpassword', {
      code,
      email,
      password,
      confirmPassword,
    });
  }

  /**
   * Change the password for a user
   *
   * @param currentPassword - current password for user
   * @param password - new password for user
   * @param confirmPassword - new password for user, should match new password
   * @param token - Antiforgery token
   */
  public changePassword(
    currentPassword: string,
    password: string,
    confirmPassword: string
    // token: string
  ): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>('/account/changepassword', {
      //_RequestVerificationToken: token,
      oldpassword: currentPassword,
      newpassword: password,
      confirmpassword: confirmPassword,
    });
  }

  /**
   * @deprecated users of this service should call the translate service themselves
   * TODO: remove after migration if not consumed directly
   */
  public getTranslatedString(
    strName: string,
    replacementVals?: Record<string, any>
  ): string {
    return this.translate.instant(strName, replacementVals);
  }

  /**
   * @deprecated consumers of the AccountService should manage their own i18n resources
   * TODO: remove after migration if not consumed directly
   *
   * NOTE: the logic in this method is always used in conjunction with the records in the
   * instance property `messageHtml`
   */
  public isMessageAnError(specialInstructions: string): boolean {
    switch (specialInstructions) {
      case 'SessionExpired':
      case 'Restricted':
      case 'ChangeLinkFail':
      case 'PasswordResetError':
        return true;
    }
    return false;
  }

  /**
   * @deprecated the auth service method should be used directly instead
   * TODO: remove after migration if not consumed directly
   */
  public goToLogin() {
    return this.authService.goToLogin(true);
  }

  /**
   * @deprecated the auth service method should be used directly instead
   * TODO: remove after migration if not consumed directly
   */
  public appendReturnUrl(prefix = '', returnUrl = ''): string {
    return this.authService.appendReturnUrl(prefix, returnUrl);
  }

  /**
   * Extract Captcha values from a given http response
   */
  private getCaptchaValues(response?: HttpResponseBase) {
    const showCaptcha =
      response?.headers?.get(showCaptchaHeaderName) === 'true';
    const validToken =
      response?.headers?.get(validUserResponseTokenHeaderName) === 'true';

    return { showCaptcha, validToken };
  }
}
